/**
 * `<IntervalPicker>` — a count box, the three intervals you last used, and a
 * dropdown holding all eleven in time order.
 *
 *     [ 1 ] [1D] [4H] [15m] [▾]
 *
 * It replaced a `<select>` of eleven options. The select was correct and cost a
 * click, a scan and a second click for the two or three intervals a person
 * actually lives in; the recents are one click, and the ones nobody uses are
 * still one click further along in the dropdown.
 *
 * ## The count box, and the trap under it
 *
 * `Frequency` is a **count and a unit**, open on the count: `2D`, `45m` and `3M`
 * are all expressible, so the box composes freely with whatever unit is showing
 * and every button says what clicking it will do — type `4` and the `1D` button
 * reads `4D`.
 *
 * The count box is untouched (`typed === null`) until it is typed into. That is
 * what lets the row rest as `[1] [1D] [4H] [15m]` — a single box cannot show
 * three different counts, and the recents are worth more as themselves than as
 * one count applied three ways. Typing switches the buttons into composing mode,
 * which is visible in the labels themselves rather than being a hidden state.
 *
 * **There is deliberately no blur handler on the box.** Reverting a typed count
 * on blur is the obvious tidy-up and it is a bug: mousedown blurs the input
 * before the click lands, so the button the user pressed while it read `4D`
 * (disabled) would re-label to `1D` (enabled) and fire — the exact silent
 * substitution the disabling is there to prevent. Escape clears it; choosing an
 * interval clears it; nothing else does.
 *
 * ## Theming and positioning
 *
 * Colours are `var(--qf-*)` only — this is chrome, and chrome inherits.
 *
 * The dropdown is `position: fixed`, measured off its trigger, as in
 * `GroupBar.tsx` and `SymbolSearch.tsx`. The toolbar row it sits in has an
 * ancestor with `overflow: hidden`, which clips an absolutely-positioned
 * popover; that has now bitten this codebase three times.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Frequency } from "@qufolio/model";

import {
  INTERVALS,
  MRU_SLOTS,
  composeInterval,
  parseCount,
  recallIntervals,
  rememberInterval,
  splitInterval,
} from "../lib/interval-mru";
import { intervalLabel, unitLabel } from "../lib/interval-label";

export interface IntervalPickerProps {
  /** The chart group's current interval — workspace state. */
  value: Frequency;
  /** Called only with an interval the contract allows. */
  onChange: (frequency: Frequency) => void;
  /**
   * The folded toolbar's form: the current unit and ▾ only. The count box and
   * the recents go, because ▾ lists every interval. See `lib/toolbar-fold.ts`.
   */
  compact?: boolean;
}

export function IntervalPicker({ value, onChange, compact = false }: IntervalPickerProps) {
  const [order, setOrder] = useState<Frequency[]>(recallIntervals);
  /** What has been typed into the count box; `null` means "not touched". */
  const [typed, setTyped] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  /**
   * The dropdown anchors to this wrapper rather than to the trigger itself: the
   * two have the same box, and a wrapper ref does not need the button to forward
   * one.
   */
  const anchorRef = useRef<HTMLDivElement | null>(null);

  /**
   * The current interval is, by definition, the most recently used one.
   *
   * Keyed on `value` rather than folded into the click handlers, so an interval
   * that arrives from anywhere else — a restored workspace, a second chart
   * group, a future agent tool call — promotes exactly as a click does.
   */
  useEffect(() => {
    setOrder(rememberInterval(value));
    setTyped(null);
  }, [value]);

  const current = splitInterval(value);
  const draft = typed?.trim() ?? "";
  const count = draft === "" ? null : parseCount(draft);
  const invalidCount = draft !== "" && count === null;

  const apply = (frequency: Frequency) => {
    setTyped(null);
    setOpen(false);
    // Promoted here as well as in the effect: choosing the interval that is
    // already current leaves `value` unchanged, so the effect would not run —
    // and re-choosing the current one is still a use.
    setOrder(rememberInterval(frequency));
    onChange(frequency);
  };

  /**
   * The two most recent intervals that are **not** the current one.
   *
   * The first slot is taken by the current interval's unit, so a history entry
   * repeating it would be a button that does nothing — the state it selects is
   * the state you are already in.
   */
  const recents = order.filter((f) => f !== value).slice(0, MRU_SLOTS - 1);

  /** Commit the typed count against the unit already showing. */
  const applyTypedCount = () => {
    if (count === null) return;
    const target = composeInterval(count, current.unit);
    if (target && target !== value) apply(target);
    else setTyped(null);
  };

  const historyButton = (frequency: Frequency) => (
    <button
      key={frequency}
      type="button"
      title={`Chart ${intervalLabel(frequency)} bars`}
      onClick={() => apply(frequency)}
      style={slotStyle(false)}
    >
      {intervalLabel(frequency)}
    </button>
  );

  return (
    // `data-tour`: the count box and the ▾ carry stable aria-labels, but the
    // unit button's title is interpolated (`Charting 1D bars`), so the row is
    // the only handle for "an interval is a count and a unit". See lib/tour.ts.
    <div style={ROW} data-tour="interval">
      {!compact && (
        <input
          value={typed ?? String(current.count)}
          // Digits only, and short. The count is open now, so this is not a
          // whitelist — three characters is simply past any interval a person
          // charts, and filtering at the source keeps the box readable.
          onChange={(event) => setTyped(event.target.value.replace(/\D/g, "").slice(0, 3))}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setTyped(null);
              return;
            }
            if (event.key === "Enter") {
              event.preventDefault();
              applyTypedCount();
            }
          }}
          inputMode="numeric"
          // No visible caption anywhere in this toolbar — the controls are
          // self-describing — so the accessible name has to come from here.
          aria-label="Interval count"
          aria-invalid={invalidCount}
          // Committed on Enter and on blur, never per keystroke. `frequency` is
          // part of ChartGroupView's `structuralKey`, so a commit per digit would
          // tear down the WebGL surface and refetch once per character — the same
          // bug the symbol box had.
          onBlur={applyTypedCount}
          title="Bar count — type a number and press Enter"
          style={{ ...COUNT_BOX, borderColor: invalidCount ? "var(--qf-negative)" : "var(--qf-border)" }}
        />
      )}

      {/* The current interval's **unit**, and only the unit — its count is in
          the box to the left, so `2D` reads as `2` then `D`. Always selected,
          because it is not a choice among the buttons: it is what is charted.
          Clicking it re-commits the same interval, which is a no-op the user
          cannot get wrong. */}
      <button
        type="button"
        aria-pressed
        title={`Charting ${intervalLabel(value)} bars`}
        onClick={() => apply(value)}
        style={slotStyle(true)}
      >
        {compact ? intervalLabel(value) : unitLabel(current.unit)}
      </button>

      {/* Two history entries, shown whole. A recent interval is somewhere to
          jump back to rather than something to edit in place, so it keeps its
          count: `2D` stays `2D` instead of splitting across two controls. */}
      {!compact && recents.map(historyButton)}

      <div ref={anchorRef} style={{ position: "relative", display: "flex" }}>
        <button
          type="button"
          aria-label="All intervals"
          aria-haspopup="listbox"
          aria-expanded={open}
          title="All intervals"
          onClick={() => setOpen((v) => !v)}
          style={{ ...slotStyle(false), minWidth: 0, padding: "2px 6px" }}
        >
          ▾
        </button>
        {open && (
          <Menu anchorRef={anchorRef} onClose={() => setOpen(false)}>
            {/* A listbox, not a stack of buttons: the recents above are buttons
                with the same names, and both a screen reader and a test need to
                be able to tell "the 1D button" from "1D in the list". */}
            <div role="listbox" aria-label="Intervals" style={{ padding: 4 }}>
              {INTERVALS.map((frequency) => (
                <button
                  key={frequency}
                  type="button"
                  role="option"
                  aria-selected={frequency === value}
                  onClick={() => apply(frequency)}
                  style={optionStyle(frequency === value)}
                >
                  {intervalLabel(frequency)}
                </button>
              ))}
            </div>
          </Menu>
        )}
      </div>
    </div>
  );
}

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  flex: "0 0 auto",
};

/**
 * Longhand `border-*` and `font-*` throughout, never the shorthands.
 *
 * The variants below differ by border colour and weight, and React warns —
 * correctly — when a longhand is dropped from an element whose shorthand is
 * still set, because the browser's answer for the removed half is then whatever
 * the shorthand said. Writing longhand everywhere means every variant sets every
 * property it cares about.
 */
const COUNT_BOX: React.CSSProperties = {
  width: 34,
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
  padding: "2px 4px",
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--qf-border)",
  borderRadius: 4,
  outline: "none",
};

const SLOT_BASE: React.CSSProperties = {
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
  padding: "2px 8px",
  minWidth: 36,
  borderRadius: 4,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--qf-border)",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
};

function slotStyle(active: boolean): React.CSSProperties {
  if (active) {
    return {
      ...SLOT_BASE,
      background: "var(--qf-accent)",
      borderColor: "var(--qf-accent)",
      color: "var(--qf-background)",
      fontWeight: 600,
    };
  }
  return SLOT_BASE;
}

function optionStyle(active: boolean): React.CSSProperties {
  return {
    display: "block",
    width: "100%",
    fontFamily: "inherit",
    fontSize: 12,
    padding: "4px 10px",
    border: 0,
    borderRadius: 4,
    background: active ? "var(--qf-selection)" : "transparent",
    color: active ? "var(--qf-accent)" : "var(--qf-text)",
    fontVariantNumeric: "tabular-nums",
    cursor: "pointer",
    textAlign: "left",
  };
}

/**
 * A dismiss-on-outside-click popover, positioned `fixed` off its trigger.
 *
 * Copied from `GroupBar.tsx` rather than imported — its version is private to
 * that module, and the shared component this wants does not exist yet. **Not
 * `absolute`**, whatever a tidy-up suggests: the toolbar sits inside a container
 * with `overflow: hidden`, and an absolutely-positioned child of one is clipped
 * by it. `fixed` leaves the clipping context entirely, at the price of having to
 * measure the trigger and of closing on scroll rather than following it.
 */
function Menu({
  anchorRef,
  children,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const menu = ref.current;
    if (!anchor || !menu) return;

    const rect = anchor.getBoundingClientRect();
    const height = menu.offsetHeight;
    const width = menu.offsetWidth;

    const below = rect.bottom + 4;
    // Flips above when there is no room below, so a chart docked at the bottom
    // of the window still shows its intervals.
    const flip = below + height > window.innerHeight && rect.top - height - 4 > 0;
    const top = flip ? rect.top - height - 4 : below;
    const left = Math.max(4, Math.min(rect.left, window.innerWidth - width - 4));

    setPos({ top, left });
  }, [anchorRef, children]);

  useEffect(() => {
    const onDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    // `pointerdown`, not `mousedown`. SciChart calls `preventDefault()` on its
    // own pointer events, which suppresses the compatibility mouse events
    // entirely — so a `mousedown` listener never fires for a click on a chart,
    // and every popover in the app stayed open when you clicked the candles to
    // dismiss it. Capture phase as well, so a handler inside the popover that
    // stops propagation cannot hide the outside click that follows.
    document.addEventListener("pointerdown", onDown, true);
    // A fixed element does not travel with a scrolling ancestor, so it would
    // detach from its trigger. Closing is honest and cheaper than tracking.
    const onScrollOrResize = () => onClose();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={{
        position: "fixed",
        // Placed off the first frame's measurement; `visibility` rather than
        // `display`, which would have no size to measure.
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        visibility: pos ? "visible" : "hidden",
        zIndex: 1000,
        background: "var(--qf-surface)",
        border: "1px solid var(--qf-border)",
        borderRadius: 6,
        boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
        minWidth: 84,
        maxHeight: "60vh",
        overflowY: "auto",
      }}
    >
      {children}
    </div>
  );
}
